import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";
import SignatureField from "../SignatureField";
import { Alert } from "react-bootstrap";

// PHI Access Notice Component
const PHIAccessNotice = () => (
  <div className="text-muted small mb-3">
    <i className="bi bi-info-circle me-1"></i>
    Logs marked with <span className="badge bg-info">PHI</span> contain
    Protected Health Information and all access is logged for HIPAA compliance.
  </div>
);

// Purpose of Use Component
const PurposeOfUseField = ({ value, onChange }) => (
  <div className="col-12 col-md-4">
    <label htmlFor="purpose_of_use" className="form-label">
      Purpose of Access <span className="text-danger">*</span>
    </label>
    <select
      id="purpose_of_use"
      className="form-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
    >
      <option value="">-- Select Purpose --</option>
      <option value="treatment">Treatment/Service Delivery</option>
      <option value="coordination">Care Coordination</option>
      <option value="documentation">Service Documentation</option>
      <option value="quality">Quality Improvement</option>
    </select>
    <small className="text-muted">Required by HIPAA for PHI access</small>
  </div>
);

// Authorization Usage Alert Component
const AuthorizationUsageAlert = ({ usage }) => {
  if (!usage || usage.is_flat_rate || usage.skip_validation) return null;

  const { status, message, hours_remaining, percent_remaining } = usage;

  const getAlertVariant = () => {
    switch (status) {
      case "red":
        return "danger";
      case "yellow":
        return "warning";
      case "green":
        return "success";
      default:
        return "info";
    }
  };

  const getIcon = () => {
    switch (status) {
      case "red":
        return <i className="bi bi-x-circle-fill text-danger"></i>;
      case "yellow":
        return <i className="bi bi-exclamation-triangle-fill text-warning"></i>;
      case "green":
        return <i className="bi bi-check-circle-fill text-success"></i>;
      default:
        return <i className="bi bi-info-circle-fill text-info"></i>;
    }
  };

  // Ensure percent_remaining is a number
  const percentValue = parseFloat(percent_remaining) || 0;
  const hoursValue = parseFloat(hours_remaining) || 0;

  return (
    <Alert variant={getAlertVariant()} className="mb-2 py-2">
      <div className="d-flex align-items-center">
        <span className="me-2" style={{ fontSize: "1.2em" }}>
          {getIcon()}
        </span>
        <span className="fw-bold">{message}</span>
        {percent_remaining !== undefined && percent_remaining !== null && (
          <span className="ms-auto text-muted small">
            ({percentValue.toFixed(1)}% remaining)
          </span>
        )}
      </div>
      {hoursValue < 30 && hoursValue > 0 && (
        <div className="mt-1 small text-muted">
          Please plan accordingly as this authorization is running low on hours.
        </div>
      )}
    </Alert>
  );
};

const JobLogForm = ({
  initialData = {},
  onSuccess,
  onCancel,
  prefillConsumerId,
  prefillDate,
  prefillUserId,
}) => {
  const { user, tenant_id, loadingUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const signatureRef = useRef(null);

  const [consumers, setConsumers] = useState([]);
  const [authOptions, setAuthOptions] = useState([]);
  const [formData, setFormData] = useState({
    consumer_id: prefillConsumerId || "",
    authorization_id: "",
    date_of_service: prefillDate || new Date().toISOString().split("T")[0],
    notes: "",
    hours_worked: "",
    is_billable: true,
    purpose_of_use: "documentation", // Default to Service Documentation
    milestone_id: "", // NEW: Add milestone_id
  });
  const [signatureData, setSignatureData] = useState(
    initialData?.signature_data || ""
  );
  const [selectedConsumerId, setSelectedConsumerId] = useState(
    prefillConsumerId || ""
  );
  const [selectedAuthorizationId, setSelectedAuthorizationId] = useState("");
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });
  const [jobCoaches, setJobCoaches] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(prefillUserId || "");
  const [authorizationUsage, setAuthorizationUsage] = useState(null);
  const [showAllConsumers, setShowAllConsumers] = useState(false); // Admin toggle to show all consumers

  // NEW: Milestone state variables
  const [authMilestones, setAuthMilestones] = useState([]);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState("");
  const [loadingMilestones, setLoadingMilestones] = useState(false);

  // Set initial selectedUserId when user loads (only if not already set by prefill)
  useEffect(() => {
    if (user?.user_id && !user?.is_admin && !selectedUserId) {
      setSelectedUserId(user.user_id);
    }
  }, [user, selectedUserId]);

  // Load job coaches for admin users
  useEffect(() => {
    if (user?.is_admin) {
      secureAxios
        .get("/api/users", {
          params: { role: "job coach", tenant_id },
        })
        .then((res) => setJobCoaches(res.data))
        .catch((err) => {
          console.error("Error loading job coaches:", err);
          setAlert({
            show: true,
            message: "Failed to load job coaches.",
            variant: "warning",
          });
        });
    }
  }, [user?.is_admin, tenant_id]);

  // Load consumers (only if billable)
  useEffect(() => {
    if (!formData.is_billable) {
      setConsumers([]);
      return;
    }

    // Admin with "Show All Consumers" toggle enabled
    if (user?.is_admin && showAllConsumers) {
      console.log("Admin loading ALL consumers for tenant");
      secureAxios
        .get("/api/consumers", {
          params: {
            tenant_id: tenant_id,
          },
        })
        .then((res) => {
          console.log("[DEBUG] All consumers response:", res.data);
          const activeConsumers = res.data.filter((c) => {
            const statusLower = (c.status || "").toLowerCase();
            return !["inactive", "archived", "deleted"].includes(statusLower);
          });
          const unique = Array.from(
            new Map(activeConsumers.map((c) => [c.consumer_id, c])).values()
          );
          setConsumers(unique);
          console.log(`[OK] Loaded ${unique.length} active consumers (all)`);
        })
        .catch((err) => {
          console.error("[X] Error loading all consumers:", err);
          setAlert({
            show: true,
            message: "Failed to load consumers.",
            variant: "danger",
          });
        });
      return;
    }

    // For regular users, use their user_id; for admins without toggle, use the selected user
    const userIdToFetch = user?.is_admin ? selectedUserId : user?.user_id;

    if (!userIdToFetch) {
      console.log("No user ID available to fetch consumers");
      setConsumers([]);
      return;
    }

    console.log("Fetching consumers for user:", userIdToFetch);

    secureAxios
      .get(
        `/api/job-coach-assignments/job-coach-assigned-consumers/${userIdToFetch}`
      )
      .then((res) => {
        console.log("[DEBUG] Consumer response:", res.data);

        if (!Array.isArray(res.data)) {
          throw new Error(
            "Expected an array of consumers but got: " +
              JSON.stringify(res.data)
          );
        }
        // Filter out inactive consumers based on status name
        const activeConsumers = res.data.filter((c) => {
          const statusLower = (c.status_name || c.status || "").toLowerCase();
          // Exclude inactive, archived, or deleted statuses
          return !["inactive", "archived", "deleted"].includes(statusLower);
        });

        const unique = Array.from(
          new Map(activeConsumers.map((c) => [c.consumer_id, c])).values()
        );
        setConsumers(unique);

        console.log(
          `[OK] Loaded ${unique.length} active consumers (filtered from ${res.data.length})`
        );
      })
      .catch((err) => {
        console.error("[X] Error loading consumers:", err);
        console.error("Error details:", err.response?.data);
        setAlert({
          show: true,
          message: "Failed to load consumers. Please check your assignments.",
          variant: "danger",
        });
      });
  }, [
    selectedUserId,
    user?.user_id,
    user?.is_admin,
    formData.is_billable,
    showAllConsumers,
    tenant_id,
  ]);

  // Load authorizations when a consumer is selected (only if billable)
  useEffect(() => {
    const fetchAuthorizations = async () => {
      if (!selectedConsumerId || !tenant_id || !formData.is_billable) {
        setAuthOptions([]);
        return;
      }
      try {
        const res = await secureAxios.get(
          `/api/auths/consumer/${selectedConsumerId}`,
          {
            params: {
              tenant_id,
              auth_type: "service",
            },
          }
        );
        const activeAuths = res.data.filter((auth) => auth.complete !== true);

        if (activeAuths.length === 0 && res.data.length > 0) {
          setAlert({
            show: true,
            message:
              "All authorizations for this consumer are completed. Please request a new authorization.",
            variant: "info",
          });
        }

        setAuthOptions(activeAuths);
      } catch (err) {
        console.error("[X] Error fetching authorizations", err);
        setAuthOptions([]);
        setAlert({
          show: true,
          message: "Failed to load authorizations.",
          variant: "warning",
        });
      }
    };

    fetchAuthorizations();
  }, [selectedConsumerId, tenant_id, formData.is_billable]);

  // Fetch authorization usage when authorization is selected
  useEffect(() => {
    const fetchAuthUsage = async () => {
      if (!selectedAuthorizationId || !formData.is_billable) {
        setAuthorizationUsage(null);
        return;
      }

      try {
        const res = await secureAxios.get(
          `/api/job-logs/authorization/${selectedAuthorizationId}/usage`
        );
        setAuthorizationUsage(res.data);
      } catch (err) {
        console.error("Error fetching authorization usage:", err);
      }
    };

    fetchAuthUsage();
  }, [selectedAuthorizationId, formData.is_billable]);

  // NEW: Load milestones for flat rate authorizations
  useEffect(() => {
    const fetchMilestones = async () => {
      if (!selectedAuthorizationId || !formData.is_billable) {
        setAuthMilestones([]);
        setSelectedMilestoneId("");
        return;
      }

      // Check if this is a flat rate authorization
      const selectedAuth = authOptions.find(
        (a) => a.authorization_id === selectedAuthorizationId
      );

      if (!selectedAuth?.flat_rate) {
        setAuthMilestones([]);
        setSelectedMilestoneId("");
        return;
      }

      setLoadingMilestones(true);
      try {
        const res = await secureAxios.get(
          `/api/auth-milestones/auth/${selectedAuthorizationId}`,
          { params: { tenant_id } }
        );
        setAuthMilestones(res.data || []);

        // If editing and we have an initial milestone_id, set it
        if (initialData?.milestone_id) {
          setSelectedMilestoneId(initialData.milestone_id);
        }
      } catch (err) {
        console.error("Error loading milestones:", err);
        setAuthMilestones([]);
      } finally {
        setLoadingMilestones(false);
      }
    };

    fetchMilestones();
  }, [
    selectedAuthorizationId,
    formData.is_billable,
    authOptions,
    tenant_id,
    initialData?.milestone_id,
  ]);

  // Load initial form data for editing
  useEffect(() => {
    if (initialData?.job_log_id) {
      console.log("[>>] Loading initial data for editing:", {
        jobLogId: initialData.job_log_id,
        hasSignature: !!initialData.signature_data,
        signatureLength: initialData.signature_data?.length,
        signaturePreview: initialData.signature_data?.substring(0, 50),
        milestone_id: initialData.milestone_id,
      });

      setFormData({
        consumer_id: initialData.consumer_id || "",
        authorization_id: initialData.authorization_id || "",
        date_of_service: initialData.date_of_service?.split("T")[0] || "",
        notes: initialData.notes || "",
        hours_worked: initialData.hours_worked || "",
        is_billable: initialData.is_billable !== false,
        purpose_of_use: initialData.purpose_of_use || "documentation",
        milestone_id: initialData.milestone_id || "", // NEW: Load milestone_id
      });
      setSelectedConsumerId(initialData.consumer_id || "");
      setSelectedAuthorizationId(initialData.authorization_id || "");

      // NEW: Set selected milestone if editing
      if (initialData.milestone_id) {
        setSelectedMilestoneId(initialData.milestone_id);
      }

      // Set signature data
      const signatureToLoad = initialData.signature_data || "";
      setSignatureData(signatureToLoad);
      console.log("[>>] Set signature data:", {
        length: signatureToLoad.length,
        preview: signatureToLoad.substring(0, 50),
      });

      // Set the user_id if editing and admin
      if (user?.is_admin && initialData.user_id) {
        setSelectedUserId(initialData.user_id);
      }
    }
  }, [initialData, user?.is_admin]);

  // Auto-hide alert after 5 seconds
  useEffect(() => {
    if (alert.show) {
      const timer = setTimeout(() => {
        setAlert({ ...alert, show: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert.show]);

  // Handle prefill from Log Sync Check widget
  useEffect(() => {
    // If we have a prefillUserId (from the widget), set the selected user first
    // This will trigger the consumers useEffect to load the right consumers
    if (prefillUserId && !initialData?.job_log_id && user?.is_admin) {
      console.log("Prefilling user_id (job coach):", prefillUserId);
      setSelectedUserId(prefillUserId);
    }

    if (prefillDate && !initialData?.job_log_id) {
      setFormData((prev) => ({
        ...prev,
        date_of_service: prefillDate,
      }));
    }
  }, [prefillUserId, prefillDate, initialData?.job_log_id, user?.is_admin]);

  // Separate effect to set consumer after consumers are loaded
  useEffect(() => {
    if (prefillConsumerId && !initialData?.job_log_id && consumers.length > 0) {
      // Check if the prefilled consumer is in the list
      const consumerExists = consumers.some(
        (c) => String(c.consumer_id) === String(prefillConsumerId)
      );

      if (consumerExists) {
        console.log("Prefilling consumer_id:", prefillConsumerId);
        setSelectedConsumerId(prefillConsumerId);
        setFormData((prev) => ({
          ...prev,
          consumer_id: prefillConsumerId,
          is_billable: true,
        }));
        // Fetch authorizations for this consumer
        fetchAuthorizationsForConsumer(prefillConsumerId);
      } else {
        console.warn(
          "Prefilled consumer not found in consumer list. Consumer may not be assigned to this job coach."
        );
        // For admins, auto-enable "show all consumers" if consumer isn't in the list
        if (user?.is_admin && !showAllConsumers) {
          console.log(
            "Admin: enabling 'show all consumers' to find prefilled consumer"
          );
          setShowAllConsumers(true);
        }
      }
    } else if (
      prefillConsumerId &&
      !initialData?.job_log_id &&
      consumers.length === 0
    ) {
      // Consumers not loaded yet, but we have a prefillConsumerId
      // Set the form data anyway - it will be validated when consumers load
      setSelectedConsumerId(prefillConsumerId);
      setFormData((prev) => ({
        ...prev,
        consumer_id: prefillConsumerId,
        is_billable: true,
      }));
      // Fetch authorizations for this consumer
      fetchAuthorizationsForConsumer(prefillConsumerId);
    }
  }, [
    prefillConsumerId,
    initialData?.job_log_id,
    consumers.length,
    user?.is_admin,
    showAllConsumers,
  ]);

  // Helper function to fetch authorizations (add if not already present)
  const fetchAuthorizationsForConsumer = async (consumerId) => {
    if (!consumerId) return;
    try {
      const res = await secureAxios.get(`/api/auths`, {
        params: { consumer_id: consumerId, active: true },
      });
      setAuthOptions(res.data);
    } catch (err) {
      console.error("Error fetching authorizations:", err);
    }
  };

  // Show loading or block if not logged in
  if (loadingUser) return <div>Loading user...</div>;
  if (!user || !tenant_id)
    return <div>[STOP] Please log in to access this form.</div>;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get the latest signature data
    let latestSig = signatureData;

    // AUTO-CAPTURE SIGNATURE BEFORE SUBMISSION
    console.log("[>>] Auto-capturing signature before submission...");
    console.log("Current signatureData:", signatureData?.substring(0, 50));

    if (signatureRef.current && signatureRef.current.getCurrentSignature) {
      // Use the new getCurrentSignature method
      const currentSig = signatureRef.current.getCurrentSignature();
      console.log("[>>] Got current signature:", {
        hasSignature: !!currentSig,
        length: currentSig?.length,
        preview: currentSig?.substring(0, 50),
      });

      if (currentSig) {
        latestSig = currentSig;
        setSignatureData(currentSig);
      }
    } else if (signatureRef.current) {
      // Fallback to old method
      const pad = signatureRef.current.getPad();
      if (pad && !pad.isEmpty()) {
        const capturedSignature = pad.toDataURL();
        latestSig = capturedSignature;
        setSignatureData(capturedSignature);
      }
    }

    console.log("[>>] Final signature for submission:", {
      hasSignature: !!latestSig,
      length: latestSig?.length,
    });

    // Validate required fields
    if (!formData.notes || !formData.notes.trim()) {
      setAlert({
        show: true,
        message: "[X] Notes are required.",
        variant: "danger",
      });
      return;
    }

    // Validate billable requirements
    if (formData.is_billable && !formData.authorization_id) {
      setAlert({
        show: true,
        message: "[X] Authorization is required for billable time.",
        variant: "danger",
      });
      return;
    }

    // NEW: Validate milestone selection for flat rate with milestones
    if (
      formData.is_billable &&
      authorizationUsage?.is_flat_rate &&
      authMilestones.length > 0 &&
      !selectedMilestoneId
    ) {
      setAlert({
        show: true,
        message: "[X] Please select a milestone for this flat rate service.",
        variant: "danger",
      });
      return;
    }

    // Check if hours exceed availability (for billable time and non-flat rate)
    if (
      formData.is_billable &&
      authorizationUsage &&
      formData.hours_worked &&
      !authorizationUsage.is_flat_rate
    ) {
      const requestedHours = parseFloat(formData.hours_worked);
      const availableHours = parseFloat(authorizationUsage.hours_remaining);

      if (requestedHours > availableHours) {
        setAlert({
          show: true,
          message: `[X] Cannot log ${requestedHours} hours. Only ${availableHours} hours remaining on this authorization.`,
          variant: "danger",
        });
        return;
      }
    }

    const payload = {
      ...formData,
      user_id: selectedUserId || user.user_id,
      tenant_id,
      signature_data: latestSig || "", // Ensure it's never undefined
      is_billable: formData.is_billable,
      purpose_of_use: formData.is_billable
        ? formData.purpose_of_use
        : "operations",
      // Clear consumer and authorization if non-billable
      consumer_id: formData.is_billable ? formData.consumer_id : null,
      authorization_id: formData.is_billable ? formData.authorization_id : null,
      // NEW: Include milestone_id
      milestone_id: selectedMilestoneId || null,
    };

    console.log("[>>] Submitting job log with payload:", {
      hasSignature: !!payload.signature_data,
      signatureLength: payload.signature_data?.length,
      milestone_id: payload.milestone_id,
    });

    try {
      if (initialData?.job_log_id) {
        await secureAxios.put(
          `/api/job-logs/${initialData.job_log_id}`,
          payload
        );
      } else {
        await secureAxios.post("/api/job-logs", payload);
      }

      setAlert({
        show: true,
        message: "[OK] Job log saved successfully.",
        variant: "success",
      });

      // Reset form for new entry
      if (!initialData?.job_log_id) {
        setFormData({
          consumer_id: "",
          authorization_id: "",
          date_of_service: new Date().toISOString().split("T")[0],
          notes: "",
          hours_worked: "",
          is_billable: true,
          purpose_of_use: "documentation",
          milestone_id: "", // NEW: Reset milestone_id
        });
        setSelectedConsumerId("");
        setSelectedAuthorizationId("");
        setAuthOptions([]);
        setSignatureData("");
        setAuthorizationUsage(null);
        // NEW: Reset milestone state
        setSelectedMilestoneId("");
        setAuthMilestones([]);

        // Clear the signature pad
        if (signatureRef.current) {
          signatureRef.current.clear();
        }
      }

      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1000);
    } catch (err) {
      console.error("[X] Error saving job log:", err);
      const errorMessage =
        err.response?.data?.error ||
        "Failed to save job log. Please try again.";
      setAlert({
        show: true,
        message: `[X] ${errorMessage}`,
        variant: "danger",
      });
    }
  };

  const handleBillableChange = (e) => {
    const isBillable = e.target.checked;
    setFormData((prev) => ({
      ...prev,
      is_billable: isBillable,
      // Clear consumer and authorization when switching to non-billable
      consumer_id: isBillable ? prev.consumer_id : "",
      authorization_id: isBillable ? prev.authorization_id : "",
      purpose_of_use: isBillable ? "documentation" : "operations",
      milestone_id: "", // NEW: Clear milestone
    }));

    if (!isBillable) {
      setSelectedConsumerId("");
      setSelectedAuthorizationId("");
      setAuthOptions([]);
      setAuthorizationUsage(null);
      // NEW: Clear milestone state
      setSelectedMilestoneId("");
      setAuthMilestones([]);
    }
  };

  // Clear signature handler
  const handleClearSignature = () => {
    console.log("[>>] Clearing signature from form");
    setSignatureData("");
    if (signatureRef.current && signatureRef.current.clear) {
      signatureRef.current.clear();
    }
  };

  return (
    <div className="container">
      <div className="row">
        <div className="col-12">
          <div className="card p-4 shadow-sm">
            <h5 className="mb-4">
              <i className="bi bi-clipboard-check me-2"></i>
              Job Coach Daily Log
            </h5>

            {/* PHI Access Notice for billable time */}
            {formData.is_billable && <PHIAccessNotice />}

            {alert.show && (
              <Alert
                variant={alert.variant}
                onClose={() => setAlert({ ...alert, show: false })}
                dismissible
              >
                {alert.message}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                {/* Debug button for testing */}
                {process.env.NODE_ENV === "development" && (
                  <div className="col-12">
                    <button
                      type="button"
                      className="btn btn-sm btn-info"
                      onClick={() => {
                        console.log("[DEBUG] Info:");
                        console.log(
                          "- signatureRef.current:",
                          signatureRef.current
                        );
                        console.log("- signatureData:", signatureData);
                        console.log("- authMilestones:", authMilestones);
                        console.log("- selectedMilestoneId:", selectedMilestoneId);
                        if (signatureRef.current) {
                          console.log(
                            "- getPad():",
                            signatureRef.current.getPad?.()
                          );
                          console.log(
                            "- getCurrentSignature():",
                            signatureRef.current.getCurrentSignature?.()
                          );
                        }
                      }}
                    >
                      Debug State
                    </button>
                  </div>
                )}

                <div className="col-12 col-md-4">
                  <label htmlFor="date_of_service" className="form-label">
                    Date of Service
                  </label>
                  <input
                    id="date_of_service"
                    className="form-control"
                    type="date"
                    name="date_of_service"
                    value={formData.date_of_service}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        date_of_service: e.target.value,
                      }))
                    }
                    required
                  />
                </div>

                <div className="col-12 col-md-4">
                  <div className="form-check mt-4">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="is_billable"
                      checked={formData.is_billable}
                      onChange={handleBillableChange}
                    />
                    <label className="form-check-label" htmlFor="is_billable">
                      Billable Time
                    </label>
                  </div>
                  {!formData.is_billable && (
                    <small className="text-muted">
                      Non-billable time will not require consumer or
                      authorization
                    </small>
                  )}
                </div>

                {user.is_admin && (
                  <div className="col-12 col-md-4">
                    <label htmlFor="job_coach" className="form-label">
                      Job Coach
                    </label>
                    <select
                      id="job_coach"
                      className="form-select"
                      value={selectedUserId}
                      onChange={(e) => {
                        const newId = e.target.value;
                        setSelectedUserId(newId);
                        setFormData((prev) => ({
                          ...prev,
                          user_id: newId,
                          consumer_id: "",
                          authorization_id: "",
                          milestone_id: "", // NEW: Reset milestone
                        }));
                        setSelectedConsumerId("");
                        setSelectedAuthorizationId("");
                        setAuthOptions([]);
                        setAuthorizationUsage(null);
                        // NEW: Reset milestone state
                        setSelectedMilestoneId("");
                        setAuthMilestones([]);
                      }}
                      required={!showAllConsumers}
                      disabled={showAllConsumers}
                    >
                      <option value="">-- Select Job Coach --</option>
                      {jobCoaches.map((jc) => (
                        <option key={jc.user_id} value={jc.user_id}>
                          {jc.name}
                        </option>
                      ))}
                    </select>
                    {showAllConsumers && (
                      <small className="text-muted">
                        Disabled when showing all consumers
                      </small>
                    )}
                  </div>
                )}

                {/* Admin toggle to show all consumers */}
                {user?.is_admin && formData.is_billable && (
                  <div className="col-12 col-md-4">
                    <div className="form-check mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="showAllConsumers"
                        checked={showAllConsumers}
                        onChange={(e) => {
                          setShowAllConsumers(e.target.checked);
                          // Reset consumer selection when toggling
                          setSelectedConsumerId("");
                          setFormData((prev) => ({
                            ...prev,
                            consumer_id: "",
                            authorization_id: "",
                            milestone_id: "", // NEW: Reset milestone
                          }));
                          setSelectedAuthorizationId("");
                          setAuthOptions([]);
                          setAuthorizationUsage(null);
                          // NEW: Reset milestone state
                          setSelectedMilestoneId("");
                          setAuthMilestones([]);
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="showAllConsumers"
                      >
                        Show All Consumers
                      </label>
                    </div>
                    <small className="text-muted">
                      {showAllConsumers
                        ? "Showing all active consumers in tenant"
                        : "Showing only assigned consumers"}
                    </small>
                  </div>
                )}

                {formData.is_billable && (
                  <>
                    <div className="col-12 col-md-4">
                      <label htmlFor="consumer" className="form-label">
                        Consumer <span className="text-danger">*</span>
                        <span className="badge bg-info ms-2">PHI</span>
                      </label>
                      <select
                        id="consumer"
                        className="form-select"
                        value={selectedConsumerId}
                        onChange={(e) => {
                          const cid = e.target.value;
                          setSelectedConsumerId(cid);
                          setFormData((prev) => ({
                            ...prev,
                            consumer_id: cid,
                          }));
                          // Reset authorization when consumer changes
                          setSelectedAuthorizationId("");
                          setFormData((prev) => ({
                            ...prev,
                            authorization_id: "",
                            milestone_id: "", // NEW: Reset milestone
                          }));
                          setAuthorizationUsage(null);
                          // NEW: Reset milestone state
                          setSelectedMilestoneId("");
                          setAuthMilestones([]);
                        }}
                        required
                      >
                        <option value="">-- Select Consumer --</option>
                        {consumers.map((c) => (
                          <option key={c.consumer_id} value={c.consumer_id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-4">
                      <label htmlFor="authorization" className="form-label">
                        Authorization <span className="text-danger">*</span>
                      </label>
                      <select
                        id="authorization"
                        className="form-select"
                        value={selectedAuthorizationId}
                        onChange={(e) => {
                          const aid = e.target.value;
                          setSelectedAuthorizationId(aid);
                          setFormData((prev) => ({
                            ...prev,
                            authorization_id: aid,
                            milestone_id: "", // NEW: Reset milestone when auth changes
                          }));
                          // NEW: Reset milestone state when auth changes
                          setSelectedMilestoneId("");
                          setAuthMilestones([]);
                        }}
                        required
                      >
                        <option value="">-- Select Authorization --</option>
                        {authOptions.map((auth) => (
                          <option
                            key={auth.authorization_id}
                            value={auth.authorization_id}
                          >
                            {auth.authorization_number} - {auth.service_name}
                            {auth.flat_rate && " (Flat Rate)"}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* NEW: Milestone Selection for Flat Rate Services */}
                {formData.is_billable &&
                  authorizationUsage?.is_flat_rate &&
                  authMilestones.length > 0 && (
                    <div className="col-12 col-md-4">
                      <label htmlFor="milestone" className="form-label">
                        Milestone <span className="text-danger">*</span>
                      </label>
                      <select
                        id="milestone"
                        className="form-select"
                        value={selectedMilestoneId}
                        onChange={(e) => {
                          setSelectedMilestoneId(e.target.value);
                          setFormData((prev) => ({
                            ...prev,
                            milestone_id: e.target.value,
                          }));
                        }}
                        required
                        disabled={loadingMilestones}
                      >
                        <option value="">-- Select Milestone --</option>
                        {authMilestones
                          .filter((m) => !m.is_invoiced)
                          .sort((a, b) => a.sequence_order - b.sequence_order)
                          .map((m) => (
                            <option key={m.milestone_id} value={m.milestone_id}>
                              {m.sequence_order}. {m.milestone_name}
                              {m.is_complete ? " [Complete]" : ""}
                            </option>
                          ))}
                        {authMilestones.filter((m) => m.is_invoiced).length >
                          0 && (
                          <optgroup label="Already Invoiced (unavailable)">
                            {authMilestones
                              .filter((m) => m.is_invoiced)
                              .sort((a, b) => a.sequence_order - b.sequence_order)
                              .map((m) => (
                                <option
                                  key={m.milestone_id}
                                  value=""
                                  disabled
                                >
                                  {m.sequence_order}. {m.milestone_name}{" "}
                                  [Invoiced]
                                </option>
                              ))}
                          </optgroup>
                        )}
                      </select>
                      <small className="text-muted">
                        Select which milestone this work applies to.
                      </small>
                    </div>
                  )}

                {/* NEW: Info message when flat rate has no milestones */}
                {formData.is_billable &&
                  authorizationUsage?.is_flat_rate &&
                  authMilestones.length === 0 &&
                  !loadingMilestones &&
                  selectedAuthorizationId && (
                    <div className="col-12">
                      <div className="alert alert-info py-2 mb-2">
                        <i className="bi bi-info-circle me-2"></i>
                        This flat rate authorization has no milestones defined -
                        billing as single unit.
                      </div>
                    </div>
                  )}

                {/* Authorization Usage Alert */}
                {formData.is_billable && authorizationUsage && (
                  <div className="col-12">
                    <AuthorizationUsageAlert usage={authorizationUsage} />
                  </div>
                )}

                <div className="col-md-5">
                  <label htmlFor="notes" className="form-label">
                    Notes <span className="text-danger">*</span>
                  </label>
                  <textarea
                    id="notes"
                    className="form-control"
                    name="notes"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    rows="3"
                    placeholder="Document the services provided, consumer progress, and any important observations"
                    required
                  />
                </div>

                <div className="col-md-2">
                  <label htmlFor="hours_worked" className="form-label">
                    Hours <span className="text-danger">*</span>
                  </label>
                  <input
                    id="hours_worked"
                    type="number"
                    step="0.01"
                    className="form-control"
                    name="hours_worked"
                    value={formData.hours_worked}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        hours_worked: e.target.value,
                      }))
                    }
                    required
                    min="0"
                    max="24"
                  />
                  {formData.is_billable &&
                    authorizationUsage &&
                    formData.hours_worked &&
                    !authorizationUsage.is_flat_rate && (
                      <small
                        className={`text-${
                          parseFloat(formData.hours_worked) >
                          parseFloat(authorizationUsage.hours_remaining || 0)
                            ? "danger"
                            : "muted"
                        }`}
                      >
                        {parseFloat(
                          authorizationUsage.hours_remaining || 0
                        ).toFixed(2)}{" "}
                        hours available
                      </small>
                    )}
                  {formData.is_billable &&
                    authorizationUsage &&
                    authorizationUsage.is_flat_rate && (
                      <small className="text-success">
                        Flat rate service - no hour limit
                      </small>
                    )}
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <label className="form-label">Consumers Signature</label>
                  <SignatureField
                    ref={signatureRef}
                    value={signatureData}
                    onChange={(data) => {
                      console.log("[>>] Signature changed in JobLogForm:", {
                        hasData: !!data,
                        length: data?.length,
                        preview: data?.substring(0, 50),
                      });
                      setSignatureData(data || "");
                    }}
                    penColor="blue"
                    backgroundColor="white"
                    disabled={false}
                    required={false}
                    maxSize={100000}
                    className="mb-0"
                  />

                  {/* Show existing signature if present */}
                  {signatureData && (
                    <div className="mt-2 p-2 bg-light rounded">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          <i className="bi bi-check-circle text-success me-1"></i>
                          Signature on file
                        </small>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={handleClearSignature}
                        >
                          <i className="bi bi-trash me-1"></i>
                          Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Debug info */}
                  {process.env.NODE_ENV === "development" && (
                    <small className="text-muted d-block mt-1">
                      Signature state:{" "}
                      {signatureData
                        ? `${signatureData.length} chars`
                        : "empty"}
                      {initialData?.job_log_id && (
                        <span className="d-block">
                          Initial signature:{" "}
                          {initialData.signature_data
                            ? `${initialData.signature_data.length} chars`
                            : "none"}
                        </span>
                      )}
                    </small>
                  )}
                </div>

                <div className="col-md-12 d-flex gap-2">
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={
                      // Disable if billable, non-flat rate, and either no hours left or exceeding available hours
                      formData.is_billable &&
                      authorizationUsage &&
                      !authorizationUsage.is_flat_rate &&
                      (parseFloat(authorizationUsage.hours_remaining || 0) <=
                        0 ||
                        (formData.hours_worked &&
                          parseFloat(formData.hours_worked) >
                            parseFloat(
                              authorizationUsage.hours_remaining || 0
                            )))
                    }
                  >
                    {initialData?.job_log_id ? "Update Log" : "Save Log"}
                  </button>
                  {onCancel && (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={onCancel}
                    >
                      Cancel
                    </button>
                  )}
                </div>

                {/* Show reason for disabled button */}
                {formData.is_billable &&
                  authorizationUsage &&
                  !authorizationUsage.is_flat_rate && (
                    <>
                      {parseFloat(authorizationUsage.hours_remaining || 0) <=
                        0 && (
                        <div className="col-12">
                          <small className="text-danger">
                            <i className="bi bi-exclamation-circle me-1"></i>
                            Cannot save: This authorization has no hours
                            remaining.
                          </small>
                        </div>
                      )}
                      {formData.hours_worked &&
                        parseFloat(formData.hours_worked) >
                          parseFloat(authorizationUsage.hours_remaining || 0) &&
                        parseFloat(authorizationUsage.hours_remaining || 0) >
                          0 && (
                          <div className="col-12">
                            <small className="text-danger">
                              <i className="bi bi-exclamation-circle me-1"></i>
                              Cannot save: Entered hours (
                              {formData.hours_worked}) exceed available hours (
                              {parseFloat(
                                authorizationUsage.hours_remaining || 0
                              ).toFixed(2)}
                              ).
                            </small>
                          </div>
                        )}
                    </>
                  )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobLogForm;